import { Fragment, useEffect, useRef, useState } from "react";
import { loadWhop } from "@whop/elements";
import {
  AddressElement,
  BrandingElement,
  CardCvcElement,
  CardExpiryElement,
  CardFields,
  CardNumberElement,
  Payments,
  WhopElements,
  usePayments as useWhopPayments,
  useWhop,
  WHOP_ELEMENTS_LOCALES,
} from "@whop/elements-react";
import { ArrowLeft, Check, ChevronDown, ChevronUp } from "lucide-react";
import PortalHeader from "../components/PortalHeader";
import Countdown from "../components/Countdown";
import styles from "./CheckoutPage.module.css";
import Button from "../components/Button";
import { useLanguage } from "../LanguageContext";
import { useAuth } from "../AuthContext";
import usePayments, { WHOP_ENVIRONMENT } from "../usePayments";

// The coin Nexus's widget opens on; the trader picks the coin and network there
const DEFAULT_COIN = "USDT";

// Card stays shown but unselectable until Whop verifies our KYC. Flip to true to restore it.
const CARD_ENABLED = false;

// `activation` (the $249 checkouts) lists crypto first, as does every checkout while
// card is disabled
function PaymentDropdown({ value, onChange, activation }) {
  const { t } = useLanguage();
  const cardOption = {
    value: "card",
    disabled: !CARD_ENABLED,
    label: !CARD_ENABLED ? t("checkout_card_soon") : activation ? (
      <>
        <span className={styles.labelLong}>{t("checkout_card_with_fee")}</span>
        <span className={styles.labelShort}>{t("checkout_card_with_fee_short")}</span>
      </>
    ) : t("checkout_card"),
  };
  const cryptoOption = {
    value: "crypto",
    label: activation ? t("checkout_crypto_plain") : t("checkout_crypto"),
  };
  const PAYMENT_OPTIONS = activation || !CARD_ENABLED ? [cryptoOption, cardOption] : [cardOption, cryptoOption];
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const selected = PAYMENT_OPTIONS.find((o) => o.value === value);

  return (
    <div className={styles.dropdownWrap} ref={ref}>
      <button
        type="button"
        className={`${styles.dropdownTrigger} ${open ? styles.dropdownTriggerOpen : ""}`}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span>{selected.label}</span>
        {open ? <ChevronUp size={16} className={styles.dropdownChevron} /> : <ChevronDown size={16} className={styles.dropdownChevron} />}
      </button>

      {open && (
        <ul className={styles.dropdownMenu} role="listbox">
          {PAYMENT_OPTIONS.map((opt, i) => (
            <Fragment key={opt.value}>
              {i > 0 && <li className={styles.dropdownDivider} role="separator" />}
              <li
                role="option"
                aria-selected={opt.value === value}
                aria-disabled={opt.disabled || undefined}
                className={`${styles.dropdownItem} ${opt.value === value ? styles.dropdownItemSelected : ""} ${opt.disabled ? styles.dropdownItemDisabled : ""}`}
                onMouseDown={() => {
                  if (opt.disabled) return;
                  onChange(opt.value);
                  setOpen(false);
                }}
              >
                <span className={styles.dropdownCheck}>
                  {opt.value === value && <Check size={14} strokeWidth={2.5} />}
                </span>
                {opt.label}
              </li>
            </Fragment>
          ))}
        </ul>
      )}
    </div>
  );
}

// List prices shown until the API's quote arrives
const LIST_PRICES = { activation: 249, funded: 250 };
// After Whop reports the payment, wait this long for the webhook to record it
const CONFIRM_POLL_MS = 2000;
const CONFIRM_TIMEOUT_MS = 90 * 1000;

// Where Whop sends the trader back after a 3-D Secure step that leaves the page. Whop only
// accepts https, so a local http portal sends none and Whop uses its own receipt page.
const returnUrl = () =>
  window.location.protocol === "https:" ? `${window.location.origin}/portfolio` : undefined;
// handleNextAction statuses that mean the card wasn't charged
const FAILED_STATUSES = ["failed", "canceled", "requires_payment_method"];

const formatUsd = (n) =>
  `$${n.toLocaleString("en-US", { minimumFractionDigits: Number.isInteger(n) ? 0 : 2, maximumFractionDigits: 2 })}`;

// A checkout priced by the API. `kind` is "activation" ($249) or "funded" ($250). Card is
// paid in Whop's card fields on our own form (payCard). Crypto (pay) opens Nexus's payment
// widget in a new tab and hands this tab to the status page (onCryptoStarted), or, while
// the API has no Nexus key, leaves for Whop's hosted crypto checkout.
// step: details (method + code) → confirming (card: waiting for the webhook) → paid
// (funded only; an activation hands straight back via onPaid) or processing (still
// unconfirmed after CONFIRM_TIMEOUT_MS)
function usePaidCheckout({ kind, method, coin, onPaid, onCryptoStarted }) {
  const { quote: fetchQuote, startCheckout, chargeCard, getCheckout } = usePayments();
  const [code, setCode] = useState(null);
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [checkout, setCheckout] = useState(null);
  const [step, setStep] = useState("details");
  // App re-creates its completion handler every render; only the latest one matters
  const onPaidRef = useRef(onPaid);
  onPaidRef.current = onPaid;

  // Re-price whenever the method or code changes. A code the API rejects is dropped.
  useEffect(() => {
    let active = true;
    setQuote(null);
    fetchQuote({ kind, method, code: code ?? "" }).then((result) => {
      if (!active) return;
      if (result.error) {
        setError(result.error);
        if (code) setCode(null);
        return;
      }
      setQuote(result);
    });
    return () => {
      active = false;
    };
  }, [kind, method, code, fetchQuote]);

  useEffect(() => {
    if (step !== "confirming") return undefined;
    let active = true;
    let timer;
    const started = Date.now();
    const poll = async () => {
      const result = await getCheckout(checkout.id);
      if (!active) return;
      if (result.checkout?.status === "Paid") {
        if (kind === "activation") onPaidRef.current();
        else setStep("paid");
      } else if (Date.now() - started >= CONFIRM_TIMEOUT_MS) setStep("processing");
      else timer = setTimeout(poll, CONFIRM_POLL_MS);
    };
    poll();
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [kind, step, checkout, getCheckout]);

  const applyCode = (value) => {
    setError(null);
    setCode(value);
  };

  // Stays busy on success, since this page is going away. Nexus has no way back to the
  // portal, so its widget gets a tab of its own, opened now while the click still counts
  // as one (a tab opened after the request would be blocked as a pop-up).
  const pay = async () => {
    setBusy(true);
    setError(null);
    const widgetTab = quote?.cryptoProvider === "nexus" ? window.open("", "_blank") : null;
    const result = await startCheckout({ kind, method, code: code ?? "", coin });
    if (result.error) {
      widgetTab?.close();
      setError(result.error);
      setBusy(false);
      return;
    }
    const { paymentUrl, purchaseUrl, id } = result.checkout;
    if (!paymentUrl) {
      widgetTab?.close();
      window.location.assign(purchaseUrl);
    } else if (widgetTab) {
      widgetTab.opener = null;
      widgetTab.location.href = paymentUrl;
      onCryptoStarted(id);
    } else {
      // Pop-ups blocked: pay in this tab; the status page is in the portal for later
      window.location.assign(paymentUrl);
    }
  };

  // Card: `mint` turns the card fields into a confirmation token, the API charges it, and
  // `finish` completes any 3-D Secure step before we wait for the webhook
  const payCard = async ({ mint, finish }) => {
    setBusy(true);
    setError(null);
    try {
      const { confirmationToken } = await mint();
      const result = await chargeCard({ kind, code: code ?? "", confirmationToken, returnUrl: returnUrl() });
      if (result.error) throw new Error(result.error);
      setCheckout(result.checkout);
      if (result.clientSecret) {
        const next = await finish(result.clientSecret);
        if (next.lastPaymentError || FAILED_STATUSES.includes(next.status)) {
          throw new Error(next.lastPaymentError?.message || "Your payment didn't go through. Please try again.");
        }
      }
      setStep("confirming");
    } catch (err) {
      setError(err.message || "Your payment didn't go through. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return {
    code,
    quote,
    error,
    busy,
    checkout,
    step,
    applyCode,
    pay,
    payCard,
  };
}

// Whop's hosted card and address inputs, styled like our .input fields. They render in
// Whop's frames, out of reach of our stylesheet and CSS variables, so the colours are
// the --border and --sage-500 tokens written out.
const cardAppearance = (theme) => {
  const dark = theme === "dark";
  const input = { borderRadius: "0px", borderWidth: "1px", borderColor: dark ? "#384040" : "#d3dbd9", backgroundColor: "transparent" };
  const focused = { borderColor: "#548d75" };
  return {
    theme: { appearance: dark ? "dark" : "light", accentColor: "jade", grayColor: "sage" },
    variables: { "--radius": "0px" },
    classes: {
      "whop-CardFieldInput": input,
      "whop-CardFieldInputFocused": focused,
      "whop-AddressFieldInput": input,
      "whop-AddressFieldSelect": input,
      // Flush with our "Billing details" heading, and labels like our .label (SemiBold 14/20)
      "whop-Address": { padding: "0px", margin: "0px" },
      "whop-AddressFieldLabel": { fontWeight: "600", fontSize: "14px", lineHeight: "20px" },
    },
  };
};

// Scope for Whop's card fields: loads Whop Elements once and holds the payment the
// fields belong to. `total` (USD) gates which cards Whop offers.
function CardPaymentScope({ accountId, total, theme, children }) {
  const { langCode } = useLanguage();
  const [elements] = useState(loadWhop);
  const locale = langCode.toLowerCase();
  return (
    <WhopElements
      elements={elements}
      environment={WHOP_ENVIRONMENT}
      appearance={cardAppearance(theme)}
      locale={WHOP_ELEMENTS_LOCALES.includes(locale) ? locale : undefined}
      toasts={false}
    >
      <Payments
        accountId={accountId}
        currency="usd"
        amount={Math.round(total * 100)}
        paymentMethodConfiguration={{ enabled: ["card"], disabled: [], include_platform_defaults: false }}
      >
        {children}
      </Payments>
    </WhopElements>
  );
}

// The original card layout (number, then expiry and CVV), with Whop's PCI-isolated
// inputs in place of ours, plus the name, country and postal code a card charge needs
function CardDetails({ onCardChange, onAddressChange }) {
  const { t } = useLanguage();
  return (
    <>
      <CardFields onChange={({ complete }) => onCardChange(complete)}>
        <div className={styles.field}>
          <span className={styles.label}>{t("checkout_card_number")}</span>
          <CardNumberElement />
        </div>
        <div className={styles.twoCol}>
          <div className={styles.field}>
            <span className={styles.label}>{t("checkout_expiry")}</span>
            <CardExpiryElement />
          </div>
          <div className={styles.field}>
            <span className={styles.label}>{t("checkout_cvv")}</span>
            <CardCvcElement />
          </div>
        </div>
      </CardFields>
      <div className={styles.field}>
        <span className={styles.label}>{t("checkout_billing")}</span>
        <AddressElement scope="minimal" name="combined" onChange={({ complete }) => onAddressChange(complete)} />
      </div>
    </>
  );
}

// COMPLETE for a card payment. Lives under <Payments> to reach Whop's handle, which
// turns the card fields into a confirmation token and finishes 3-D Secure.
function CardPayButton({ disabled, onPay, children }) {
  const payments = useWhopPayments();
  const whop = useWhop();
  const { user } = useAuth();
  return (
    <Button
      type="button"
      className={styles.completeBtn}
      disabled={disabled || !payments || !whop}
      onClick={() =>
        onPay({
          mint: () => payments.createConfirmationToken({ billingDetails: { email: user.email } }),
          finish: (clientSecret) => whop.payments.handleNextAction({ clientSecret, returnUrl: returnUrl() }),
        })
      }
    >
      {children}
    </Button>
  );
}

// `activation` charges the $249 activation price, otherwise the $250 funded account;
// `activationDeadline` adds the post-qualification claim countdown. `onCryptoStarted(id)`
// takes over once a Nexus crypto payment opens. `theme`
// ("light" | "dark") styles Whop's card fields.
export default function CheckoutPage({
  onBack,
  onComplete,
  onCryptoStarted,
  onNavigate,
  navItems,
  activePath,
  activation = false,
  activationDeadline,
  theme,
}) {
  const { t } = useLanguage();
  const isActivation = activation;
  const kind = isActivation ? "activation" : "funded";
  const [paymentMethod, setPaymentMethod] = useState(isActivation || !CARD_ENABLED ? "crypto" : "card");
  const [discountCode, setDiscountCode] = useState("");
  const [cardComplete, setCardComplete] = useState(false);
  const [addressComplete, setAddressComplete] = useState(false);
  const paid = usePaidCheckout({ kind, method: paymentMethod, coin: DEFAULT_COIN, onPaid: onComplete, onCryptoStarted });

  // Figures come from the API quote; the total reads "—" until it arrives
  const price = paid.quote?.listPrice ?? LIST_PRICES[kind];
  const cardFee = paid.quote?.cardFee ?? 0;
  const discount = paid.quote?.discount || null;
  const total = paid.quote?.total ?? null;

  // Re-pricing (a code applied) briefly clears the quote. The card fields stay mounted
  // through it on the last known account and total, so nothing the trader typed is lost.
  const lastQuote = useRef(null);
  if (paid.quote) lastQuote.current = paid.quote;
  const whopAccountId = lastQuote.current?.accountId;
  // Card is entered on our own form once the API names the Whop account; until then (or
  // if it never does) card falls back to the embedded checkout like crypto
  const cardInline = paymentMethod === "card" && Boolean(whopAccountId);

  const changeMethod = (method) => {
    setPaymentMethod(method);
    setCardComplete(false);
    setAddressComplete(false);
  };

  function handleApplyDiscount() {
    const code = discountCode.trim();
    if (code && !paid.code) paid.applyCode(code);
  }

  const formBody = (
    <div className={styles.body}>
      {/* ── Left: payment form ── */}
      <div className={styles.formSection}>
        {paid.step === "details" ? (
          <>
            <div className={styles.field}>
              <label className={styles.label}>{t("checkout_payment_method")}</label>
              <PaymentDropdown value={paymentMethod} onChange={changeMethod} activation={isActivation} />
            </div>

            {cardInline ? (
              <CardDetails onCardChange={setCardComplete} onAddressChange={setAddressComplete} />
            ) : paymentMethod === "crypto" ? (
              <div className={styles.cryptoBox}>
                <p className={styles.cryptoText}>{t("checkout_crypto_whop_text")}</p>
              </div>
            ) : (
              // Whop's checkout on the next step collects the card details
              <div className={styles.cryptoBox}>
                <p className={styles.cryptoText}>{t("checkout_card_whop_text")}</p>
              </div>
            )}

            <div className={styles.field}>
              <label className={styles.label}>{t("checkout_discount")}</label>
              <div className={styles.discountRow}>
                <input
                  className={styles.input}
                  type="text"
                  placeholder="CODE"
                  value={discountCode}
                  onChange={(e) => {
                    setDiscountCode(e.target.value.toUpperCase());
                    if (paid.code) paid.applyCode(null);
                  }}
                />
                <button
                  type="button"
                  className={`${styles.applyBtn} ${paid.code ? styles.applyBtnApplied : ""}`}
                  onClick={handleApplyDiscount}
                  disabled={!discountCode.trim() || Boolean(paid.code)}
                >
                  {t("checkout_apply")}
                </button>
              </div>
            </div>

            {/* Whop's merchant-of-record notice: required, the card fields wait for it */}
            {cardInline && <BrandingElement />}
          </>
        ) : (
          <div className={styles.cryptoBox} role="status">
            <p className={styles.cryptoText}>
              {t(
                {
                  confirming: "checkout_confirming",
                  paid: "checkout_funded_received",
                  processing: "checkout_processing",
                }[paid.step],
              )}
            </p>
            {paid.step === "paid" && (
              <Button type="button" onClick={onComplete}>{t("checkout_back_portfolio")}</Button>
            )}
            {paid.step === "processing" && (
              <Button type="button" onClick={onBack}>{t("checkout_back_portfolio")}</Button>
            )}
          </div>
        )}

        {paid.error && <p className={styles.formError} role="alert">{paid.error}</p>}
      </div>

      {/* ── Right: summary ── */}
      <div className={styles.summarySection}>
        <h2 className={styles.summaryTitle}>{t("checkout_summary")}</h2>

        <div className={styles.summaryRow}>
          <span>{isActivation ? t("checkout_activation_account") : t("checkout_account")}</span>
          <span className={styles.summaryValue}>{formatUsd(price)}</span>
        </div>

        {cardFee > 0 && (
          <div className={styles.summaryRow}>
            <span>{t("checkout_card_fee")}</span>
            <span className={styles.summaryValue}>{formatUsd(cardFee)}</span>
          </div>
        )}

        {discount && (
          <div className={styles.summaryRow}>
            <span>{t("checkout_discount_line")}</span>
            <span className={`${styles.discountValue} ${styles.summaryValue}`}>-{formatUsd(discount)}</span>
          </div>
        )}

        <hr className={styles.divider} />

        <div className={styles.totalRow}>
          <span>{t("checkout_total")}</span>
          <span>{total == null ? "—" : formatUsd(total)}</span>
        </div>

        {paid.step === "details" && (
          <div className={styles.completeBtnWrap}>
            {cardInline ? (
              <CardPayButton
                onPay={paid.payCard}
                disabled={paid.busy || !paid.quote || !cardComplete || !addressComplete}
              >
                {t("checkout_complete")}
              </CardPayButton>
            ) : (
              <Button
                type="button"
                className={styles.completeBtn}
                onClick={paid.pay}
                disabled={paid.busy || !paid.quote}
              >
                {t("checkout_complete")}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className={styles.page}>
      <div className={styles.pageFrame}>
        <PortalHeader activePath={activePath} navItems={navItems} onNavigate={onNavigate} logoOnly />
      <div className={styles.inner}>
        <div className={styles.titleRow}>
          <button type="button" className={styles.backBtn} onClick={onBack} aria-label="Go back">
            <ArrowLeft size={20} strokeWidth={2.5} />
          </button>
          <h1 className={styles.title}>{t("checkout_title")}</h1>
        </div>

        {activationDeadline != null && (
          <div className={styles.claimBanner}>
            <p className={styles.claimTitle}>{t("checkout_claim_title")}</p>
            <Countdown deadline={activationDeadline} />
          </div>
        )}

        {cardInline ? (
          // Whop's provider has to wrap both columns: the card fields on the left and the
          // pay button in the summary
          <CardPaymentScope accountId={whopAccountId} total={total ?? lastQuote.current.total} theme={theme}>
            {formBody}
          </CardPaymentScope>
        ) : (
          formBody
        )}
      </div>
      </div>
    </div>
  );
}
